import { Chart, ChartOption } from '@/components/Base/Chart.tsx';
import { LabelImportant } from '@mui/icons-material';
import { Box } from '@mui/material';

const ColumnChartEntradasSaidasIpca = ({ rawData }) => {
  const anos = rawData.map((item) => `${item.year}`);
  const entradas = rawData.map((item) => item.entry);
  const saidas = rawData.map((item) => item.exit);
  const ipca = rawData.map((item) => item.ipca_value);
  const reajuste = rawData.map((item) => `${item.adjustment}`);
  const tendencia = rawData.map((item) => item.tendency);

  const series = [
    {
      data: entradas,
      type: 'bar',
      stack: 'a',
      name: 'Entradas de alunos',
      barWidth: '25%',
      itemStyle: {
        borderRadius: [10, 10, 0, 0],
        color: 'rgba(21, 171, 81, 1)',
      },
      label: {
        position: 'top',
        show: true,
        padding: [5, 10],
        borderRadius: [5],
        distance: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(21, 171, 81, 0.5)',
        borderWidth: 1,
        fontSize: 12,
        fontWeight: 'bold',
        formatter: (params) => `${params.value}`,
      },
    },
    {
      data: saidas,
      type: 'bar',
      stack: 'b',
      name: 'Saídas de alunos',
      barWidth: '25%',
      itemStyle: {
        borderRadius: [10, 10, 0, 0],
        color: 'rgba(224, 74, 76, 1)',
      },
      label: {
        show: true,
        position: 'top',
        padding: [5, 10],
        borderRadius: [5],
        distance: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(224, 74, 76, 0.5)',
        borderWidth: 1,
        fontSize: 12,
        fontWeight: 'bold',
        formatter: (params) => `${params.value}`,
      },
    },
    {
      data: tendencia,
      type: 'line',
      name: 'Tendência',
      symbol: 'circle',
      symbolSize: 5,
      lineStyle: {
        type: 'dashed',
        color: 'black',
        width: 2,
      },
      label: {
        show: false,
      },
    },
    {
      data: reajuste,
      type: 'line',
      name: '% de Ajuste',
      lineStyle: {
        color: '#b7a7ca',
        width: 2,
      },
      symbol: 'circle',
      symbolSize: [12, 12],
      smooth: true,
      itemStyle: {
        color: '#A56EA533',
      },
      label: {
        show: true,
        position: 'bottom',
        color: '#b7a7ca',
        formatter: (params) => `${params.value}%`,
        distance: 10,
        padding: [5, 0],
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#b7a7ca',
        borderWidth: 1,
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 'bold',
      },
    },
    {
      data: ipca,
      type: 'line',
      name: 'IPCA Nacional',
      lineStyle: {
        color: '#FDC500',
        width: 2,
      },
      itemStyle: {
        color: '#795300',
      },
      symbol: 'circle',
      symbolSize: [12, 12],
      smooth: true,
      label: {
        show: true,
        position: 'top',
        color: '#795300',
        formatter: (params) => `${params.value}%`,
        distance: 20,
        padding: [5, 0],
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#FDC500',
        borderWidth: 1,
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 'bold',
      },
    },
  ];

  const option = {
    title: {
      text: 'IPCA por ano e saída e entradas de alunos',
      textStyle: {
        fontSize: '16px',
        fontWeight: 'bold',
      },
      top: 10,
      left: 'left',
      padding: [0, 20],
    },
    legend: {
      data: [
        'Entradas de alunos',
        'Saídas de alunos',
        'Tendência',
        '% de Ajuste',
        'IPCA Nacional'
      ],
      right: '0%',
      top: '5%',
      textStyle: {
        fontSize: 12,
        fontWeight: 'normal',
      },
      itemGap: 20,
      itemWidth: 12,
      itemHeight: 12,
      padding: [0, 20],
      orient: 'horizontal',
      align: 'right',
    },
    grid: {
      top: '15%',
      bottom: '15%',
      left: '3%',
      right: '5%',
      containLabel: true
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => `${value}`,
        margin: 15,
        fontSize: 12,
        fontWeight: 'bold',
      },
      min: 0,
      max: (value) => Math.ceil(value.max * 1.3),
      splitLine: {
        show: true,
        lineStyle: {
          color: '#E0E6F1',
        }
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#B1B2B6',
        }
      },
    },
    xAxis: {
      type: 'category',
      data: anos,
      axisLabel: {
        margin: 40,
        fontSize: 12,
        fontWeight: 'bold',
        interval: 0,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#E0E0E0',
        }
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
          color: '#fff',
          fontSize: 12,
          fontWeight: 'bold',
        }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#E0E0E0',
      borderWidth: 1,
      textStyle: {
        fontSize: 12,
        color: '#333',
      },
      padding: [10, 15],
      formatter: (params) => {
        let result = `<div style="font-weight: bold; margin-bottom: 8px; font-size: 14px; color: #333;">${params[0].name}</div>`;
        params.forEach(param => {
          const value = param.seriesName.includes('%') ? `${param.value}%` : param.value;
          result += `<div style="display: flex; align-items: center; margin: 5px 0; font-size: 12px;">
            <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px; border-radius: 50%"></span>
            <span style="font-weight: bold; color: #333;">${param.seriesName}:</span>
            <span style="margin-left: 5px; color: #666;">${value}</span>
          </div>`;
        });
        return result;
      }
    },
    series,
  } as ChartOption;

  return (
    <Box sx={{ 
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      width: '100%',
      height: '500px',
      '& .echarts-for-react': {
        width: '100%',
        height: '100%'
      }
    }}>
      <Chart option={option} />
    </Box>
  );
};

export default ColumnChartEntradasSaidasIpca;
